import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { Phone, Chip } from "./Mockups";
import { HERO } from "../data/content";
import { useLang } from "../lib/lang";
import { scrollToId } from "../lib/smooth";

const Badge = ({ text }) => (
  <div className="relative grid h-28 w-28 place-items-center sm:h-32 sm:w-32" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0 h-full w-full">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text fill="#f4f4f5" fontSize="8.2" fontFamily="JetBrains Mono" letterSpacing="1.6"><textPath href="#circ">{text}</textPath></text>
    </svg>
    <span className="h-3 w-3 rounded-full bg-[var(--acid)] shadow-[0_0_24px_var(--acid)]" />
  </div>
);

const Stage3D = ({ ready }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div
      onMouseMove={move}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative mx-auto h-[440px] w-full max-w-[460px] [perspective:1200px] sm:h-[560px]"
      initial={{ opacity: 0, scale: 0.9, y: 60 }}
      animate={ready ? { opacity: 1, scale: 1, y: 0 } : undefined}
      transition={{ duration: 1.4, delay: 0.5, ease: [0.2, 0.7, 0.1, 1] }}
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <div style={{ transform: "translateZ(-80px) rotate(-9deg)" }} className="absolute left-0 top-10 w-[55%] overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-2xl">
          <img src="/work/mock_stationery.jpg" alt="Minieri stationery" className="aspect-[4/5] w-full object-cover" />
        </div>
        <div style={{ transform: "translateZ(-40px) rotate(8deg)" }} className="absolute bottom-6 right-0 w-[52%] overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-2xl">
          <img src="/work/mock_ikigai.jpg" alt="Ikigai identity" className="aspect-square w-full object-cover" />
        </div>
        <div style={{ transform: "translateZ(70px)" }} className="absolute inset-0 flex items-center justify-center">
          <div className="float-y w-[46%]"><Phone src="/video/skater.jpg" video="/video/skater.mp4" play alt="Motion graphics reel" /></div>
        </div>
        <div style={{ transform: "translateZ(120px)" }} className="absolute right-2 top-16"><Chip>Motion · Reel</Chip></div>
        <div style={{ transform: "translateZ(110px)" }} className="absolute bottom-24 left-0"><Chip className="!bg-[var(--acid)] !text-[var(--ink)]">Brand · Motion</Chip></div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = ({ ready }) => {
  const { tr } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden px-4 pb-16 pt-32 sm:px-6 lg:px-8 lg:pb-24 lg:pt-40">
      <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-[var(--acid)] opacity-[0.07] blur-[140px]" />
      <div className="relative mx-auto max-w-7xl">
        <motion.div style={{ y }} className="relative z-0 min-w-0">
          <motion.p initial={{ opacity: 0 }} animate={ready ? { opacity: 1 } : undefined} transition={{ delay: 0.2 }} className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400" data-testid="hero-eyebrow">
            <span className="mr-3 inline-block h-2 w-2 rounded-full bg-[var(--signal)]" />{tr(HERO.eyebrow)}
          </motion.p>
          <h1 data-testid="hero-title" className="font-name text-[19vw] font-medium leading-[0.86] tracking-[-0.035em] xl:text-[13.5rem]">
            <MaskLine play={ready} delay={0.1}>Camila</MaskLine>
            <MaskLine play={ready} delay={0.22} className="pl-[8vw] xl:pl-40"><span className="italic text-[var(--acid)]">Eterovic</span></MaskLine>
          </h1>
        </motion.div>
        <div className="relative z-10 grid items-center gap-10 lg:-mt-16 lg:grid-cols-2">
        <div className="min-w-0">
          <MaskLine play={ready} delay={0.38} className="mt-4">
            <span className="font-display text-3xl font-normal text-white sm:text-4xl lg:text-5xl">{tr(HERO.tagline)}</span>
          </MaskLine>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : undefined} transition={{ delay: 0.7, duration: 0.9 }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            {tr(HERO.sub)}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={ready ? { opacity: 1, y: 0 } : undefined} transition={{ delay: 0.85, duration: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-works" onClick={() => scrollToId("work")} className="group flex items-center gap-3 rounded-full bg-[var(--acid)] px-7 py-4 text-sm font-semibold text-[var(--ink)] transition-transform hover:scale-[1.03]">
              {tr(HERO.cta1)} <ArrowDownRight size={18} className="transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-cta-contact" onClick={() => scrollToId("contact")} className="group flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold transition-colors hover:bg-white hover:text-[var(--ink)]">
              {tr(HERO.cta2)} <ArrowUpRight size={18} />
            </button>
            <div className="ml-2 hidden sm:block"><Badge text={tr(HERO.badge)} /></div>
          </motion.div>
        </div>
        <Stage3D ready={ready} />
        </div>
      </div>
    </section>
  );
};
